import { payersApi, payrollSchedulesApi } from "@api";
import { queriesPayers, queriesPayrollSchedules } from "@query";
import {
  PayerPayrollStatusUpdate,
  PayrollScheduleType,
  ResponseError
} from "@wingspanhq/wingspan-api-sdk";
import { WSMutationConfig } from "@ws-react-query";
import { useWSMutation } from "../../../../query/helpers";
import { QUERY_PAYROLL_SETTINGS } from "../../../../query/payments/keys";
import { selectorPayrollScheduleUpdate } from "../../../../shared/selectors/selectorPayrollScheduleUpdate";
import { FormData } from "./types";

/**
 * Saves the payroll-schedule step across the two resources that hold it: the
 * on/off switch on payer-settings, the cadence on the payer's contractor payroll
 * schedule.
 *
 * Both writes go straight to the SDK rather than through the registry's own
 * mutation hooks, so the reads are invalidated once, after the last write — a
 * registry mutation refreshes its own reads on success, which would have the
 * switch's reads refetching while the cadence write is still in flight and
 * briefly showing payroll enabled on the cadence the payer just replaced.
 *
 * One mutation for both writes also gives the step a single loading flag and a
 * single error, and leaves it where it is on a failure instead of advancing on a
 * half-saved setting.
 */
export const useMutationSubmit = (
  config?: WSMutationConfig<void, ResponseError, FormData>
) =>
  useWSMutation<void, ResponseError, FormData>(
    async data => {
      const settings =
        await queriesPayers.getCurrentPayerSettings.fetchQueryData(undefined);

      // Every payer-settings write is elevated and prompts for a second factor,
      // so the switch is only written when the payer actually moved it —
      // otherwise saving a cadence change would demand an MFA challenge.
      if (data.runsPayroll !== settings?.payrollSettings?.runsPayroll) {
        await payersApi.updateCurrentPayerSettings({
          currentPayerSettingsUpdate: {
            payrollSettings: data.runsPayroll
              ? // Enabling also activates payroll, as the v1 write did: a payer
                // whose settings sit at Draft would otherwise have the switch on
                // and no payroll running.
                { runsPayroll: true, status: PayerPayrollStatusUpdate.ACTIVE }
              : { runsPayroll: false }
          }
        });
      }

      // With payroll off there is no cadence to keep: the payer's existing
      // schedule is left as it stands, and the switch is what stops the runs.
      if (!data.runsPayroll) {
        return;
      }

      await payrollSchedulesApi.updatePayrollSchedule({
        type: PayrollScheduleType.CONTRACTOR_VENDOR,
        payrollScheduleUpdate: selectorPayrollScheduleUpdate(data)
      });
    },
    {
      dependencies: [
        queriesPayers.getCurrentPayerSettings.queryKeyBase,
        queriesPayrollSchedules.getPayrollSchedule.queryKeyBase,
        queriesPayrollSchedules.listPayrollSchedules.queryKeyBase,
        // The payables screens the payer lands on next still read the payroll
        // settings through v1.
        QUERY_PAYROLL_SETTINGS
      ],
      ...config
    }
  );
